/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useTenantTranslation } from "hooks/formatters";
import { useCallback, useEffect, useState } from "react";
import { useDisableHotkeyScopes } from "../hooks/useDisableHotkeyScopes";
import { usePauseVideoWhileOpen } from "../hooks/usePauseVideoWhileOpen";
import { ScrapEyeLayoutMap } from "components/scrapEyeLayout/ScrapEyeLayoutMap";
import { useHotkeyContext } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { useAreaHotkeys } from "../hooks/useAreaHotkeys";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import { useWaitForKeyInput } from "../hooks/useWaitForKeyInput";
import { AreaColorMap } from "components/scrapEyeLayout/scrapEyeLayoutCommon";
import {
  isSpecialKey,
  keyCodeToKeyName,
} from "components/scrapEyeLayout/hotkeys/keyCodeConversion";
import { useAreaLabel } from "../hooks/useAreaLabel";
import { LABELLING_HOTKEY_SCOPES } from "components/manualReview/hotkeyScopes";

type AreaHotkeyConfigModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  disableScopes?: readonly string[];
  areaColors?: AreaColorMap;
  location?: string;
}>;

export const AreaHotkeyConfigModal = ({
  open,
  onClose,
  disableScopes = LABELLING_HOTKEY_SCOPES,
  areaColors,
  location,
}: AreaHotkeyConfigModalProps) => {
  const { t } = useTenantTranslation();
  const { setAreaHotkey, resetAreaHotkey } = useHotkeyContext();
  const { area_hotkeys: defaultAreaHotkeys } = useScrapEyeMetadata();
  const currentAreaHotkeys = useAreaHotkeys();
  const resolveAreaLabel = useAreaLabel(location);

  const [selectedArea, setSelectedArea] = useState<string | null>(null);

  useDisableHotkeyScopes(disableScopes, open);
  usePauseVideoWhileOpen(open);

  const findAreaWithHotkey = useCallback(
    (targetKey: string): string | null => {
      if (!currentAreaHotkeys) return null;
      const upperKey = targetKey.toUpperCase();
      for (const [area, hotkey] of Object.entries(currentAreaHotkeys)) {
        if (hotkey.toUpperCase() === upperKey) {
          return area;
        }
      }
      return null;
    },
    [currentAreaHotkeys]
  );

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (!selectedArea) return false;
      if (isSpecialKey(event.code)) return false;

      const key = keyCodeToKeyName(event.code);

      const existingArea = findAreaWithHotkey(key);
      const oldHotkey = currentAreaHotkeys?.[selectedArea];

      if (existingArea && existingArea !== selectedArea && oldHotkey) {
        // Swap: give the other area our old hotkey
        setAreaHotkey(existingArea, oldHotkey);
      }

      setAreaHotkey(selectedArea, key);
      setSelectedArea(null);
    },
    [selectedArea, findAreaWithHotkey, currentAreaHotkeys, setAreaHotkey]
  );

  const [waitingForInput, startWaiting, stopWaiting] =
    useWaitForKeyInput(handleKeyPress);

  const handleSelectArea = useCallback(
    (area: string) => {
      setSelectedArea(area);
      startWaiting();
    },
    [startWaiting]
  );

  const handleResetArea = useCallback(
    (area: string) => {
      resetAreaHotkey(area);
      setSelectedArea(null);
      stopWaiting();
    },
    [resetAreaHotkey, stopWaiting]
  );

  const handleResetAll = useCallback(() => {
    Object.keys(defaultAreaHotkeys ?? {}).forEach((area) => {
      resetAreaHotkey(area);
    });
    setSelectedArea(null);
    stopWaiting();
  }, [defaultAreaHotkeys, resetAreaHotkey, stopWaiting]);

  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- bidirectional state reset on dialog close
      setSelectedArea(null);
      stopWaiting();
    }
  }, [open, stopWaiting]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg">
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <span>{t("configureAreaHotkeys")}</span>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="text"
              size="small"
              startIcon={<RestartAltIcon />}
              onClick={handleResetAll}
            >
              {t("resetAll")}
            </Button>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary">
            {waitingForInput && selectedArea
              ? t("pressKeyForArea", { area: resolveAreaLabel(selectedArea) })
              : t("clickAreaToChangeHotkey")}
          </Typography>

          <ScrapEyeLayoutMap
            onSelectArea={handleSelectArea}
            selectedArea={selectedArea}
            areaColors={areaColors}
            location={location}
          />

          {selectedArea ? (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="center"
            >
              <Typography variant="body2">
                {t("currentHotkey")}:{" "}
                <strong>{currentAreaHotkeys?.[selectedArea]}</strong>
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  handleResetArea(selectedArea);
                }}
              >
                {t("resetToDefault")}
              </Button>
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
